import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  ConfirmModalComponent,
  InfoBannerComponent,
  ModalComponent,
  SegmentedControlComponent,
  SegmentedControlItem,
  SpinnerComponent,
  TagVariant,
} from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { StripeAdapter } from '../../adapters/stripe.adapter';
import { AppState } from '../../app.reducer';
import {
  StripeBillingInterval,
  StripeProductType,
  FirstBookingPaymentMethod,
} from '../../enums/stripe.enum';
import { StripeProduct } from '../../interfaces/stripe';
import { ScheduledPlanChange } from '../../interfaces/subscription';
import { BookingConfirmationService } from '../../services/booking-confirmation.service';
import { selectSubscription } from '../../store/subscription/subscription.reducer';
import { isImmediatePlanChange } from '../../utils/is-immediate-plan-change.util';
import { translateBillingPeriod } from '../../utils/billing-period-display.util';
import {
  filterBillingIntervalOptions,
  resolveBookingInterval,
  siblingPackageInterval,
} from '../../utils/package-billing-interval.util';
import { BasePlanSelectorComponent } from '../booking-selectors/base-plan-selector/base-plan-selector.component';
import { ButtonComponent } from '../button/button.component';
import { InvoiceBookingConfirmModalComponent } from '../invoice-booking-confirm-modal/invoice-booking-confirm-modal.component';
import { PaymentCheckoutComponent } from '../payment-checkout/payment-checkout.component';
import { PaymentMethodChoiceComponent } from '../payment-method-choice/payment-method-choice.component';

enum BasePlanKey {
  STARTER = 'starter',
  GROWTH = 'growth',
  SCALE = 'scale',
}

@Component({
  selector: 'app-base-booking-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DatePipe,
    TranslocoModule,
    ModalComponent,
    ConfirmModalComponent,
    PaymentCheckoutComponent,
    PaymentMethodChoiceComponent,
    ButtonComponent,
    SegmentedControlComponent,
    InfoBannerComponent,
    SpinnerComponent,
    InvoiceBookingConfirmModalComponent,
    BasePlanSelectorComponent,
  ],
  templateUrl: './base-booking-modal.component.html',
  styleUrl: './base-booking-modal.component.scss',
})
export class BaseBookingModalComponent {
  private readonly modal = viewChild<ModalComponent>('modal');
  private readonly downgradeConfirmModal = viewChild<ConfirmModalComponent>(
    'downgradeConfirmModal',
  );
  private readonly cancelScheduleConfirmModal =
    viewChild<ConfirmModalComponent>('cancelScheduleConfirmModal');
  private readonly invoiceConfirmModal =
    viewChild.required<InvoiceBookingConfirmModalComponent>('invoiceConfirm');
  readonly paymentCheckout = viewChild(PaymentCheckoutComponent);
  private readonly paymentMethodChoice =
    viewChild<PaymentMethodChoiceComponent>('paymentMethodChoice');
  private readonly stripeAdapter = inject(StripeAdapter);
  private readonly store = inject(Store<AppState>);
  private readonly toastsService = inject(ToastsService);
  private readonly transloco = inject(TranslocoService);
  private readonly bookingConfirmation = inject(BookingConfirmationService);
  public readonly planChanged = output<void>();

  public readonly StripeBillingInterval = StripeBillingInterval;
  public readonly BasePlanKey = BasePlanKey;
  private readonly allBillingIntervalOptions: ReadonlyArray<
    SegmentedControlItem<StripeBillingInterval>
  > = [
    {
      label: 'company-tool.snippets.base-booking-modal.interval-month',
      value: StripeBillingInterval.MONTH,
    },
    {
      label: 'company-tool.snippets.base-booking-modal.interval-year',
      value: StripeBillingInterval.YEAR,
      tag: {
        label: 'company-tool.snippets.base-booking-modal.interval-year-save',
        type: TagVariant.GREEN,
      },
    },
  ];

  public isPaymentStep = signal(false);
  public isPaymentMethodStep = signal(false);
  public products = signal<StripeProduct[]>([]);
  public readonly productsLoaded = signal(false);
  public billingInterval = signal(StripeBillingInterval.MONTH);
  public selectedPriceId = signal<string | null>(null);
  public bookingInProgress = signal(false);
  public cancelScheduleBusy = signal(false);
  public onPaymentCompletedFunction = signal(() => {});
  public readonly selectionLocked = signal(false);

  private readonly subscription = this.store.selectSignal(selectSubscription);
  private pendingCheckoutSessionId: string | null = null;
  pendingDowngradePriceId: string | null = null;
  private pendingInvoiceProduct: StripeProduct | null = null;
  private catalogRequestId = 0;
  private readonly catalogCache = new Map<
    StripeBillingInterval,
    StripeProduct[]
  >();

  /**
   * True while a plan change API call runs on the selection step.
   * @returns {boolean}
   */
  readonly isUpdating = computed(
    () => this.bookingInProgress() && !this.isPaymentStep(),
  );

  readonly baseSubscription = computed(
    () => this.subscription()?.subscriptionSummary?.baseSubscription ?? null,
  );

  readonly isFree = computed(() => {
    const summary = this.subscription()?.subscriptionSummary;
    return !!summary?.isFree;
  });

  readonly hasPaidSubscription = computed(
    () => !this.isFree() && !!this.baseSubscription()?.name,
  );

  readonly currentInterval = computed((): StripeBillingInterval | null => {
    const interval = this.baseSubscription()?.interval;
    if (interval === 'year') return StripeBillingInterval.YEAR;
    if (interval === 'month') return StripeBillingInterval.MONTH;
    return null;
  });

  /**
   * Interval of other paid package items. Mixed intervals cannot be booked here.
   * @returns {StripeBillingInterval | null}
   */
  readonly siblingInterval = computed(() =>
    siblingPackageInterval(
      this.subscription()?.subscriptionSummary,
      StripeProductType.BASIC,
    ),
  );

  /**
   * Month/year tabs, without intervals locked by a sibling package item.
   * @returns {ReadonlyArray<SegmentedControlItem<StripeBillingInterval>>}
   */
  readonly billingIntervalOptions = computed(() =>
    filterBillingIntervalOptions(
      this.allBillingIntervalOptions,
      this.siblingInterval(),
    ),
  );

  readonly currentLevel = computed(() => {
    if (this.isFree()) return 0;
    return this.levelFromName(this.baseSubscription()?.name ?? null);
  });

  readonly scheduledBasicChange = computed((): ScheduledPlanChange | null => {
    const changes =
      this.subscription()?.subscriptionSummary?.scheduledPlanChanges ?? [];
    return (
      changes.find(
        (change) => change.productType === StripeProductType.BASIC,
      ) ?? null
    );
  });

  readonly canCancelSchedule = computed(() => {
    const actions =
      this.subscription()?.subscriptionSummary?.planActions?.[
        StripeProductType.BASIC
      ];
    return !!actions?.canCancelSchedule && !!this.scheduledBasicChange();
  });

  readonly modalTitleKey = computed(() => {
    if (this.isPaymentMethodStep()) {
      return 'company-tool.snippets.payment-method-choice.title';
    }
    return this.hasPaidSubscription()
      ? 'company-tool.snippets.base-booking-modal.title-change'
      : 'company-tool.snippets.base-booking-modal.title-upgrade';
  });

  readonly selectedProduct = computed(() => {
    const priceId = this.selectedPriceId();
    if (!priceId) return null;
    return this.products().find((product) => product.id === priceId) ?? null;
  });

  readonly currentBasePriceId = computed(
    () =>
      this.products().find((product) => this.isCurrentPlan(product))?.id ??
      null,
  );

  readonly isSelectedCurrentPlan = computed(() => {
    const selected = this.selectedProduct();
    return !!selected && this.isCurrentPlan(selected);
  });

  readonly selectedIsImmediate = computed(() => {
    const selected = this.selectedProduct();
    if (!selected) return true;
    return this.isImmediateChange(selected);
  });

  readonly confirmLabelKey = computed(() => {
    if (!this.hasPaidSubscription()) {
      return 'company-tool.snippets.base-booking-modal.confirm-book';
    }
    if (this.selectedIsImmediate()) {
      return 'company-tool.snippets.base-booking-modal.confirm-upgrade';
    }
    return 'company-tool.snippets.base-booking-modal.confirm-downgrade';
  });

  readonly downgradeEffectiveDate = computed(() => {
    const base = this.baseSubscription();
    return (
      base?.validUntil ?? base?.nextBillingDate ?? base?.cancelAtIfNow ?? null
    );
  });

  /**
   * Opens the modal on the billing tab of the current base plan,
   * or the sibling package interval when monthly cannot be mixed.
   * @returns {void}
   */
  public open(): void {
    const currentInterval = resolveBookingInterval({
      ownInterval: this.currentInterval(),
      siblingInterval: this.siblingInterval(),
      fallback: StripeBillingInterval.MONTH,
    });
    this.selectedPriceId.set(null);
    this.selectionLocked.set(false);
    this.catalogCache.clear();
    this.catalogRequestId++;
    this.billingInterval.set(currentInterval);
    this.isPaymentStep.set(false);
    this.isPaymentMethodStep.set(false);
    this.bookingInProgress.set(false);
    this.pendingCheckoutSessionId = null;
    this.pendingDowngradePriceId = null;
    this.loadProducts();
    this.modal()?.open();
  }

  /**
   * Closes the modal and resets the payment step.
   * @returns {void}
   */
  public close(): void {
    this.modal()?.close();
  }

  /**
   * Switches the catalog billing interval.
   * @param {StripeBillingInterval} interval Selected interval
   * @returns {void}
   */
  public setBillingInterval(interval: StripeBillingInterval): void {
    if (
      this.bookingInProgress() ||
      this.selectionLocked() ||
      this.billingInterval() === interval
    ) {
      return;
    }
    const lockedInterval = this.siblingInterval();
    if (lockedInterval && interval !== lockedInterval) return;
    this.billingInterval.set(interval);
    this.selectedPriceId.set(null);
    const cached = this.catalogCache.get(interval);
    if (cached) {
      this.applyCatalog(cached);
      return;
    }
    this.loadProducts();
  }

  /**
   * Updates the selected catalog price.
   * @param {unknown} value Radio card value (price id)
   * @returns {void}
   */
  public onPlanSelect(value: unknown): void {
    if (typeof value !== 'string') return;
    this.selectedPriceId.set(value);
  }

  /**
   * Resolves the display title for a catalog price.
   * @param {StripeProduct} product Catalog price
   * @returns {string}
   */
  public productTitle(product: StripeProduct): string {
    return product.product?.name ?? product.id;
  }

  /**
   * Monthly display amount in major currency units (yearly prices divided by 12).
   * @param {StripeProduct} product Catalog price
   * @returns {number}
   */
  public monthlyDisplayAmount(product: StripeProduct): number {
    return product.unit_amount / 100;
  }

  /**
   * Whether Growth should show the recommended badge (not when it is the current plan).
   * @param {StripeProduct} product Catalog price
   * @returns {boolean}
   */
  public isRecommendedPlan(product: StripeProduct): boolean {
    return (
      !this.isCurrentPlan(product) &&
      this.planKey(product) === BasePlanKey.GROWTH
    );
  }

  /**
   * Maps a catalog price to a known base plan key for feature copy.
   * @param {StripeProduct} product Catalog price
   * @returns {BasePlanKey | null}
   */
  public planKey(product: StripeProduct): BasePlanKey | null {
    const fromName = this.planKeyFromName(product.product?.name ?? null);
    if (fromName) return fromName;
    const level = this.subLevelOf(product);
    if (level === 1) return BasePlanKey.STARTER;
    if (level === 2) return BasePlanKey.GROWTH;
    if (level >= 3) return BasePlanKey.SCALE;
    return null;
  }

  /**
   * Whether the catalog option matches the company's current base plan.
   * @param {StripeProduct} product Catalog price
   * @returns {boolean}
   */
  public isCurrentPlan(product: StripeProduct): boolean {
    if (!this.hasPaidSubscription()) return false;
    const currentInterval = this.currentInterval();
    if (currentInterval && this.billingInterval() !== currentInterval) {
      return false;
    }

    const currentMaxJobs = this.baseSubscription()?.maxJobs ?? null;
    const productMaxJobs = this.maxJobsOf(product);
    if (
      currentMaxJobs != null &&
      productMaxJobs > 0 &&
      currentMaxJobs === productMaxJobs
    ) {
      return true;
    }

    const currentKey = this.planKeyFromName(
      this.baseSubscription()?.name ?? null,
    );
    const productKey = this.planKey(product);
    if (currentKey && productKey) {
      return currentKey === productKey;
    }

    const currentName = (this.baseSubscription()?.name ?? '').toLowerCase();
    const productName = (product.product?.name ?? '').toLowerCase();
    if (currentName && productName) {
      if (currentName === productName) return true;
      if (
        productName.includes(currentName) ||
        currentName.includes(productName)
      ) {
        return true;
      }
    }

    const productLevel = this.subLevelOf(product);
    const currentLevel = this.currentLevel();
    return (
      productLevel > 0 && currentLevel > 0 && productLevel === currentLevel
    );
  }

  /**
   * Whether selecting the option would apply immediately.
   * Fewer sub_level is always period-end. Otherwise cheaper amount is period-end.
   * @param {StripeProduct} product Catalog price
   * @returns {boolean}
   */
  public isImmediateChange(product: StripeProduct): boolean {
    if (!this.hasPaidSubscription()) return true;
    if (this.isCurrentPlan(product)) return true;

    const currentInterval = this.currentInterval();
    const currentLevel = this.currentLevel();
    const nextInterval = this.billingInterval();
    const nextLevel = this.subLevelOf(product);

    if (!currentInterval) return true;

    return isImmediatePlanChange(
      currentInterval,
      currentLevel,
      nextInterval,
      nextLevel,
      this.baseSubscription()?.cost ?? 0,
      product.unit_amount,
    );
  }

  /**
   * Confirms the selected plan (opens downgrade confirm when needed).
   * @returns {void}
   */
  public confirmSelection(): void {
    const selected = this.selectedProduct();
    if (!selected || this.bookingInProgress() || this.isSelectedCurrentPlan()) {
      return;
    }
    if (this.hasPaidSubscription() && !this.isImmediateChange(selected)) {
      this.pendingDowngradePriceId = selected.id;
      this.selectionLocked.set(true);
      this.downgradeConfirmModal()?.modal.open();
      return;
    }
    this.applyConfirmedPlan(selected);
  }

  /**
   * Applies a confirmed scheduled downgrade.
   * @returns {void}
   */
  public confirmDowngrade(): void {
    const priceId = this.pendingDowngradePriceId;
    this.pendingDowngradePriceId = null;
    this.selectionLocked.set(false);
    const product = this.products().find((item) => item.id === priceId);
    if (!product) return;
    this.applyConfirmedPlan(product);
  }

  /**
   * Continues an invoice-confirmed base plan booking.
   * @returns {void}
   */
  onInvoiceBookingConfirmed(): void {
    const product = this.pendingInvoiceProduct;
    this.pendingInvoiceProduct = null;
    this.selectionLocked.set(false);
    if (!product) {
      return;
    }
    if (!this.bookingConfirmation.invoicePayment()) {
      this.bookingInProgress.set(true);
      this.bookingConfirmation.enableInvoicePayment().subscribe({
        next: () => this.applyPlanChange(product),
        error: () => this.bookingInProgress.set(false),
      });
      return;
    }
    this.applyPlanChange(product);
  }

  /**
   * Continues after the first-booking payment-method step.
   * @returns {void}
   */
  confirmPaymentMethod(): void {
    const choice = this.paymentMethodChoice();
    const product = this.pendingInvoiceProduct;
    if (!choice || !product || this.bookingInProgress()) {
      return;
    }
    this.bookingInProgress.set(true);
    choice.prepareSelection().subscribe({
      next: (method) => {
        this.bookingInProgress.set(false);
        this.isPaymentMethodStep.set(false);
        if (method === FirstBookingPaymentMethod.INVOICE) {
          this.openInvoiceConfirm(product);
          return;
        }
        this.applyPlanChange(product);
      },
      error: () => this.bookingInProgress.set(false),
    });
  }

  /**
   * Confirms invoice costs when needed, then applies the plan change.
   * @param {StripeProduct} product Selected catalog price
   * @returns {void}
   */
  private applyConfirmedPlan(product: StripeProduct): void {
    this.pendingInvoiceProduct = product;
    if (this.bookingConfirmation.invoicePayment()) {
      this.openInvoiceConfirm(product);
      return;
    }
    this.bookingInProgress.set(true);
    this.bookingConfirmation.resolveNeedsPaymentMethodChoice().subscribe({
      next: (needsChoice) => {
        this.bookingInProgress.set(false);
        if (needsChoice) {
          this.isPaymentMethodStep.set(true);
          return;
        }
        this.applyPlanChange(product);
      },
      error: () => {
        this.bookingInProgress.set(false);
        this.applyPlanChange(product);
      },
    });
  }

  /**
   * Opens the invoice booking confirmation for a catalog price.
   * @param {StripeProduct} product Selected catalog price
   * @returns {void}
   */
  private openInvoiceConfirm(product: StripeProduct): void {
    this.selectionLocked.set(true);
    this.invoiceConfirmModal().open(
      this.bookingConfirmation.toProductConfirmData(
        product,
        translateBillingPeriod(
          this.transloco,
          product,
          this.billingInterval(),
        ),
      ),
    );
  }

  /**
   * Opens the confirm dialog before canceling a scheduled plan change.
   * @returns {void}
   */
  public openCancelScheduleConfirm(): void {
    if (this.cancelScheduleBusy() || this.isUpdating()) return;
    this.cancelScheduleConfirmModal()?.modal.open();
  }

  /**
   * Cancels a pending scheduled base plan change.
   * @returns {void}
   */
  public cancelScheduledChange(): void {
    if (this.cancelScheduleBusy()) return;
    this.cancelScheduleBusy.set(true);
    this.stripeAdapter
      .cancelScheduledPlanChange(StripeProductType.BASIC)
      .subscribe({
        next: () => {
          this.cancelScheduleBusy.set(false);
          this.toastsService.info(
            'company-tool.snippets.base-booking-modal.schedule-canceled',
          );
          this.planChanged.emit();
        },
        error: () => {
          this.cancelScheduleBusy.set(false);
          this.toastsService.alert(
            'company-tool.views.settings.company.payment.sections.subscriptions.action-error',
          );
        },
      });
  }

  /**
   * Returns from checkout to plan selection.
   * @returns {void}
   */
  public backToSelection(): void {
    if (
      this.isPaymentStep() &&
      this.bookingConfirmation.needsPaymentMethodChoice()
    ) {
      this.paymentCheckout()?.destroy();
      this.isPaymentStep.set(false);
      this.isPaymentMethodStep.set(true);
      this.bookingInProgress.set(false);
      this.pendingCheckoutSessionId = null;
      return;
    }
    if (this.isPaymentStep()) {
      this.paymentCheckout()?.destroy();
    }
    this.isPaymentStep.set(false);
    this.isPaymentMethodStep.set(false);
    this.bookingInProgress.set(false);
    this.pendingCheckoutSessionId = null;
    this.selectionLocked.set(false);
  }

  /**
   * Books the selected Base price through change-subscription.
   * @param {StripeProduct} product Selected catalog price
   * @returns {void}
   */
  private applyPlanChange(product: StripeProduct): void {
    this.changeExistingPlan(product);
  }

  /**
   * Books a Base price via change-subscription. Checkout only when Stripe
   * still needs a first payment method.
   * @param {StripeProduct} product Selected catalog price
   * @returns {void}
   */
  private changeExistingPlan(product: StripeProduct): void {
    this.bookingInProgress.set(true);
    this.stripeAdapter
      .changeSubscription(product.id, window.location.href)
      .subscribe({
        next: (result) => {
          if (result.needsCheckout && result.clientSecret) {
            this.pendingCheckoutSessionId = result.checkoutSessionId ?? null;
            this.onPaymentCompletedFunction.set(() => {
              this.bookingConfirmation.rememberPaymentMethodOnFile();
              this.stripeAdapter
                .applyBookedPrice(
                  product.id,
                  1,
                  this.pendingCheckoutSessionId ?? undefined,
                )
                .subscribe({
                  next: () => this.onChangeSucceeded(true),
                  error: () => this.onChangeSucceeded(true),
                });
            });
            this.isPaymentStep.set(true);
            this.mountChangeCheckout(
              result.clientSecret,
              result.checkoutSessionId,
            );
            return;
          }

          this.onChangeSucceeded(!!result.upgraded);
        },
        error: () => {
          this.toastsService.alert(
            'company-tool.views.settings.company.payment.sections.subscriptions.action-error',
          );
          this.bookingInProgress.set(false);
        },
      });
  }

  private onChangeSucceeded(upgraded: boolean): void {
    if (upgraded) {
      this.bookingConfirmation.showSuccessToast();
    } else {
      this.toastsService.info(
        'company-tool.snippets.base-booking-modal.success-schedule',
      );
    }
    this.planChanged.emit();
    this.close();
    this.bookingInProgress.set(false);
  }

  /**
   * Loads Base catalogs for both intervals.
   * @returns {void}
   */
  private loadProducts(): void {
    this.productsLoaded.set(false);
    const requestId = ++this.catalogRequestId;
    this.stripeAdapter
      .getSelfBookingProducts(StripeProductType.BASIC)
      .subscribe({
        next: (products) => {
          if (requestId !== this.catalogRequestId) return;
          const paid = products
            .filter((product) => product.unit_amount > 0)
            .sort((a, b) => {
              const levelDiff = this.subLevelOf(a) - this.subLevelOf(b);
              if (levelDiff !== 0) return levelDiff;
              const jobsDiff = this.maxJobsOf(a) - this.maxJobsOf(b);
              if (jobsDiff !== 0) return jobsDiff;
              return a.unit_amount - b.unit_amount;
            });
          for (const interval of [
            StripeBillingInterval.MONTH,
            StripeBillingInterval.YEAR,
          ]) {
            this.catalogCache.set(
              interval,
              paid.filter((product) => product.recurring?.interval === interval),
            );
          }
          this.applyCatalog(
            this.catalogCache.get(this.billingInterval()) ?? [],
          );
        },
        error: () => {
          if (requestId !== this.catalogRequestId) return;
          this.products.set([]);
          this.productsLoaded.set(true);
        },
      });
  }

  /**
   * Applies a catalog and seeds selection from the live plan.
   * @param {StripeProduct[]} filtered Priced catalog for the active interval
   * @returns {void}
   */
  private applyCatalog(filtered: StripeProduct[]): void {
    const current = this.findCurrentProduct(filtered);
    // Set selection before cards mount so the group keeps it while registering.
    this.selectedPriceId.set(
      current?.id ??
        (!this.hasPaidSubscription()
          ? (this.findGrowthProduct(filtered)?.id ?? filtered[0]?.id ?? null)
          : null),
    );
    this.products.set(filtered);
    this.productsLoaded.set(true);
  }

  /**
   * Finds the catalog price that matches the company's current base plan.
   * @param {StripeProduct[]} products Catalog prices
   * @returns {StripeProduct | undefined}
   */
  private findCurrentProduct(
    products: StripeProduct[],
  ): StripeProduct | undefined {
    return products.find((product) => this.isCurrentPlan(product));
  }

  /**
   * Finds the recommended Growth catalog price.
   * @param {StripeProduct[]} products Catalog prices
   * @returns {StripeProduct | undefined}
   */
  private findGrowthProduct(
    products: StripeProduct[],
  ): StripeProduct | undefined {
    return products.find(
      (product) => this.planKey(product) === BasePlanKey.GROWTH,
    );
  }

  private subLevelOf(product: StripeProduct): number {
    const fromPrice = +(product.metadata?.sub_level ?? 0);
    if (fromPrice > 0) return fromPrice;
    const nested = product.product as
      { metadata?: Record<string, string> } | undefined;
    return +(nested?.metadata?.sub_level ?? 0);
  }

  /**
   * Reads max_jobs from price or product metadata.
   * @param {StripeProduct} product Catalog price
   * @returns {number}
   */
  private maxJobsOf(product: StripeProduct): number {
    const fromPrice = +(product.metadata?.max_jobs ?? 0);
    if (fromPrice > 0) return fromPrice;
    const nested = product.product as
      { metadata?: Record<string, string> } | undefined;
    return +(nested?.metadata?.max_jobs ?? 0);
  }

  private levelFromName(name: string | null): number {
    const key = this.planKeyFromName(name);
    if (key === BasePlanKey.STARTER) return 1;
    if (key === BasePlanKey.GROWTH) return 2;
    if (key === BasePlanKey.SCALE) return 3;
    return 0;
  }

  /**
   * Maps a subscription/product name to a known base plan key.
   * @param {string | null} name Plan name
   * @returns {BasePlanKey | null}
   */
  private planKeyFromName(name: string | null): BasePlanKey | null {
    const normalized = (name ?? '').toLowerCase();
    if (normalized.includes('starter')) return BasePlanKey.STARTER;
    if (normalized.includes('growth')) return BasePlanKey.GROWTH;
    if (normalized.includes('scale')) return BasePlanKey.SCALE;
    return null;
  }

  private mountChangeCheckout(
    clientSecret: string,
    checkoutSessionId?: string | null,
  ): void {
    if (this.paymentCheckout()) {
      void this.paymentCheckout()?.mountFromClientSecret(
        clientSecret,
        checkoutSessionId,
      );
    } else {
      setTimeout(
        () => this.mountChangeCheckout(clientSecret, checkoutSessionId),
        10,
      );
    }
  }
}
